import { useIntl } from "@components/Intl"
import { useNotificationsUpdate } from "@components/Notifications"
import imgix from "@config/imgix"
import { ArticleQuery } from "@generated"
import { NormalizedRelatedCombination } from "@lib/hooks/helpers/get-normalized-related-combinations"
import { useAddArticleCombinationToCart } from "@lib/hooks/use-add-article-combination-to-cart"
import { useAddArticleToCart } from "@lib/hooks/use-add-article-to-cart"
import * as qs from "query-string"
import React, { useEffect } from "react"
import Detail from "./Detail"
import { getCustomizeUrl } from "./helpers/get-customize-url"
import { dataLayerViewItem } from "@lib/data-layer-events/view-item"
import { useCookies } from "@components/Cookies/hooks/use-cookies"
import { dataLayerAddToCart } from "@lib/data-layer-events/add-to-cart"

export type ProductType = "article" | "articleCombination"

interface Props {
  lang: string
  uid: string
  pid: string
  relatedCombinationId?: string
  articlesId: string[]
  productType: ProductType
  article?: ArticleQuery["choicesArticle"]
  relatedCombination?: NormalizedRelatedCombination
  description?: string | null
}

const singular = {
  rings: "ring",
  earrings: "earring",
  bracelets: "bracelet",
  necklaces: "necklace",
}

const Product: React.FC<Props> = ({
  uid,
  pid,
  productType,
  article,
  relatedCombination,
  relatedCombinationId,
  articlesId,
  description,
}) => {
  const { messages } = useIntl()
  const { cookiesAreSet } = useCookies()
  const {
    mutateAsync: addArticleToCart,
    isLoading: addArticleToCartIsLoading,
  } = useAddArticleToCart()
  const {
    mutateAsync: addArticleCombinationToCart,
    isLoading: addArticleCombinationToCartIsLoading,
  } = useAddArticleCombinationToCart()
  const { addNotification } = useNotificationsUpdate()
  let returnFilters = ""
  if (typeof window !== "undefined") {
    const params = qs.parse(window.location.search)
    if (params && params["overview_filters"]) {
      returnFilters = `?${params["overview_filters"] as string}`
    }
  }

  useEffect(() => {
    if (cookiesAreSet) {
      if (article) {
        dataLayerViewItem({
          id: article.id,
          name: article.name,
          price: article.price,
        })
      }

      if (relatedCombination) {
        dataLayerViewItem({
          id: relatedCombination.combination.id,
          name: relatedCombination.combination.name,
          price: relatedCombination.combination.price,
        })
      }
    }
  }, [cookiesAreSet, article, relatedCombination])

  if (productType === "article") {
    if (!article) {
      // This never happens, because it's catched a pages/**/[pid]
      // Just for TS
      return null
    }
    const breadcrumbs = [
      {
        href: messages.navigation.collection.url,
        as: messages.navigation.collection.url,
        label: messages.navigation.collection.title,
      },
      {
        href: messages.navigation.separateItems.url,
        as: messages.navigation.separateItems.url,
        label: messages.navigation.separateItems.title,
      },
      {
        href: messages.navigation.separateItemsDetail.url + returnFilters,
        as: messages.navigation[uid].url + returnFilters,
        label: messages.navigation[uid].title,
      },
      {
        href: "",
        as: "",
        label: article.name,
      },
    ]
    let properties = {}
    if (article.style) {
      properties["style"] = article.style
    }
    if (article.size) {
      properties["size"] = article.size
    }
    if (article.cut) {
      properties["cut"] = article.cut
    }

    return (
      <Detail
        breadcrumbs={breadcrumbs}
        properties={properties}
        images={[
          imgix({
            key: article.id,
            type: "article",
            args: { w: 750, h: 750 },
          }),
        ]}
        thumbnails={[
          imgix({
            key: article.id,
            type: "article",
            args: { w: 75, h: 75 },
          }),
        ]}
        name={article.name}
        style={article.style}
        price={article.price}
        uid={uid}
        pid={pid}
        articlesId={articlesId}
        slug={article.slug}
        type={article.component}
        productType={productType}
        isLoading={addArticleToCartIsLoading}
        description={description}
        onAddToCart={async () => {
          if (article && article.price && article.component) {
            await addArticleToCart({
              articleId: pid,
            })
            dataLayerAddToCart({
              id: article.id,
              name: article.name,
              price: article.price,
            })
            addNotification({
              type: "success",
              text: `${article.name} ${messages.notifications.addedToCart}`,
            })
          }
        }}
      />
    )
  }
  if (productType === "articleCombination") {
    if (!relatedCombination) {
      // This never happens, because it's catched a pages/**/[pid]
      // Just for TS
      return null
    }
    const breadcrumbs = [
      {
        href: messages.navigation.collection.url,
        as: messages.navigation.collection.url,
        label: messages.navigation.collection.title,
      },
      {
        href: messages.navigation.collectionDetail.url + returnFilters,
        as: messages.navigation[uid].url + returnFilters,
        label: messages.navigation[uid].title,
      },
      {
        href: "",
        as: "",
        label: relatedCombination.combination.name,
      },
    ]

    const type = singular[uid]
    const customizeUrl = getCustomizeUrl(relatedCombination.combination, type)

    let properties = {}
    if (relatedCombination.combination.style) {
      properties["style"] = relatedCombination.combination.style
    }
    if (relatedCombination.combination.size) {
      properties["size"] = relatedCombination.combination.size
    }
    if (relatedCombination.combination.material) {
      properties["material"] = relatedCombination.combination.material
    }

    return (
      <Detail
        breadcrumbs={breadcrumbs}
        properties={properties}
        images={
          relatedCombination.combination.images
            ? relatedCombination.combination.images.map((image) =>
                imgix({
                  key: image.path.replace(".png", ""),
                  args: { w: 750, h: 750 },
                  type: "articleCombination",
                })
              )
            : []
        }
        thumbnails={
          relatedCombination.combination.images
            ? relatedCombination.combination.images.map((image) =>
                imgix({
                  key: image.path.replace(".png", ""),
                  args: { w: 75, h: 75 },
                  type: "articleCombination",
                })
              )
            : []
        }
        name={relatedCombination.combination.name}
        material={relatedCombination.combination.material}
        price={relatedCombination.combination.price}
        uid={uid}
        articlesId={articlesId}
        pid={pid}
        slug={relatedCombination.combination.slug}
        productType={productType}
        description={description}
        type={type}
        isLoading={addArticleCombinationToCartIsLoading}
        customizeUrl={customizeUrl}
        onAddToCart={async () => {
          if (relatedCombination.combination) {
            dataLayerAddToCart({
              id: relatedCombination.combination.id,
              name: relatedCombination.combination.name,
              price: relatedCombination.combination.price,
            })
            await addArticleCombinationToCart({
              articleId: relatedCombination.combination.id,
            })

            addNotification({
              type: "success",
              text: `${relatedCombination.combination.name} ${messages.notifications.addedToCart}`,
            })
          }
        }}
        variants={relatedCombination.variants || []}
        relatedCombinationId={relatedCombinationId}
      />
    )
  }

  return null
}

export default Product
